import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { RolesService } from "../../../core/services/roles.service";
import { TuiAlertService, TuiButton, TuiError, TuiLoader } from "@taiga-ui/core";
import { TuiInputModule } from "@taiga-ui/legacy";
import { TuiCheckbox, TuiStatus, TuiSwitch } from "@taiga-ui/kit";
import { CommonModule, NgForOf } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { FormControl } from '@angular/forms';
import { TuiPlatform } from '@taiga-ui/cdk';
import type { TuiSizeS } from '@taiga-ui/core';
import { DisableUnlessPermissionDirective } from "../../../core/directives/disable-unless-permission.directive";


interface Permission {
  label: string;
  value: string;
  checked: boolean;
  disabled?: boolean;
}

@Component({
  selector: "app-add-role",
  imports: [
    DisableUnlessPermissionDirective,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgForOf,
    TuiInputModule,
    TuiCheckbox,
    TuiButton
  ],
  templateUrl: "./add-role.component.html",
  styleUrl: "./add-role.component.scss",
})
export class AddRoleComponent implements OnInit {

  breadcrumbs: any[] = [];
  name: string = "";
  nameInvalid: boolean = false;
  id: string | null = null;
  isLoading: boolean = false;
  permissions: string[] = [];

  private readonly alerts = inject(TuiAlertService);

  permissionGroups: {
    key: string;
    title: string;
    description: string;
    permissions: Permission[];
  }[] = [
      { key: "User", title: "User Management", description: "User Permissions", permissions: [] },
      { key: "Role", title: "Role Management", description: "Role Permissions", permissions: [] },
      { key: "Client", title: "Client Management", description: "Client Permissions", permissions: [] },
      { key: "Nurse", title: "Nurse Management", description: "Nurse Permissions", permissions: [] },
      { key: "Invoice", title: "Invoice Management", description: "Invoice Permissions", permissions: [] },
      { key: "Report", title: "Report Management", description: "Report Permissions", permissions: [] },
      { key: "Extra Charges", title: "Extra Charges Management", description: "Extra Charges Permissions", permissions: [] },
      { key: "Salary Charges", title: "Salary and Charges Management", description: "Salary and Charges Permissions", permissions: [] },
      { key: "Shift", title: "Shift Management", description: "Shifts Permissions", permissions: [] },
      { key: "Leave", title: "Leave Management", description: "Leave Permissions", permissions: [] },
    ];

  constructor(
    private rolesService: RolesService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit() {
    this.setupBreadcrumbs();
    this.route.paramMap.subscribe((params) => {
      this.id = params.get("id");
      this.loadPermissions();
    });
  }

  setupBreadcrumbs() {
    this.breadcrumbs = [
      { title: "Roles", route: "/admin/roles" },
      { title: this.id ? "Edit Role" : "Add New Role" },
    ];
  }

  validateName() {
    this.nameInvalid = !this.name.trim();
  }

  loadPermissions() {
    this.isLoading = true;
    this.rolesService.getPermissionsByGroup().subscribe({
      next: (permissions: any) => {
        console.log(permissions);
        this.permissionGroups.forEach((group) => {
          group.permissions = permissions
            .filter((p: any) => {
              if (group.key === "Leave") {
                return p.name.endsWith(group.key);
              }
              return p.name.startsWith(group.key);
            })
            .map((p: any) => {
              let label = "";
              if (group.key === "Leave") {
                label = p.name.split(" ").slice(0, -1).join(" ");
              } else {
                label = p.name.split(" ").slice(1).join(" ");
              }
              return {
                label,
                value: p._id,
                checked: false,
              };
            });
        });
      },
      error: (error) => console.error("Error fetching permissions:", error),
      complete: () => {
        this.isLoading = false;
        if (this.id) {
          this.loadRoleDetails(this.id);
        }
      },
    });
  }

  loadRoleDetails(id: string) {
    this.rolesService.getOneRoleById(id).subscribe({
      next: (roleData: any) => {
        this.name = roleData.name;
        this.applyRolePermissions(roleData.permissions);
      },
      error: (error) => console.error("Failed to load role details:", error),
    });
  }

  applyRolePermissions(rolePermissions: string[]) {
    this.isLoading = true;
    this.permissionGroups.forEach((group) => {
      group.permissions.forEach((perm) => {
        perm.checked = rolePermissions.includes(perm.value);
      });
    });
    this.collectPermissions();
    this.isLoading = false;
  }

  collectPermissions() {
    this.permissions = this.permissionGroups
      .flatMap((group) => group.permissions)
      .filter((perm) => perm.checked)
      .map((perm) => perm.value);
  }

  addEditRole() {
    this.isLoading = true;
    const payload = { name: this.name.toUpperCase(), permissions: this.permissions };

    const request = this.id
      ? this.rolesService.updateRole({ id: this.id, ...payload })
      : this.rolesService.createRole(payload);

    request.subscribe({
      next: () => {
        this.alerts.open(`Role ${this.id ? "updated" : "created"} successfully!`, {
          label: "Success",
          appearance: "success",
        }).subscribe();
        this.router.navigate(["/admin/roles"]);
      },
      error: (error) => {
        console.error("Full error object:", error);
        const errorMessage = error?.message || error?.error?.message || "An unexpected error occurred.";
        if (error.status === 403) {
          this.alerts.open(errorMessage, { label: "Error", appearance: "negative" }).subscribe();
        } else {
          this.alerts.open("An unexpected error occurred.", { label: "Error", appearance: "negative" }).subscribe();
        }
      },
      complete: () => {
        if (this.id) this.loadRoleDetails(this.id);
        this.isLoading = false;
      },
    });
  }

  updatePermissions(groupKey: string, permissions: Permission[]): void {
    const group = this.permissionGroups.find((g) => g.key === groupKey);
    if (!group) {
      console.error(`Group with key '${groupKey}' not found.`);
      return;
    }
    group.permissions.forEach((perm) => this.enforceViewDependency(group.permissions, perm));
    this.collectPermissions();
  }

  enforceViewDependency(permissions: Permission[], perm: Permission): void {
    const viewPerm = permissions.find((p) => p.label === 'View');

    if (
      ['Update', 'Create', 'Delete', 'Reset Password', 'Change Status'].includes(perm.label) &&
      perm.checked
    ) {
      if (viewPerm && !viewPerm.checked) {
        viewPerm.checked = true;
        viewPerm.disabled = true;
      }
    } else if (perm.label === 'View') {
      if (!perm.checked) {
        permissions.forEach((p) => {
          if (['Update', 'Create', 'Delete', 'Reset Password', 'Change Status'].includes(p.label)) {
            p.checked = false;
          }
        });
        if (viewPerm) {
          viewPerm.disabled = false;
        }
      }
    } else if (viewPerm && perm.checked) {
      viewPerm.checked = true;
      viewPerm.disabled = true;
    }
  }

  isInvalid(): boolean {
    return !this.name.trim() || !this.permissions.length;
  }
}
